import {
  Input,
  Button,
  Typography,
  Textarea,
} from "@material-tailwind/react";
import Toaster from "../../components/Toaster";
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ShowLoader from "@/components/Loader";
import Select from 'react-select';
import { ArrowLeftIcon, PlusCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { addBrand, resetStatus } from "@/store/slices/brandSlice";
import FilePreview from "@/components/FilePreview";
import ScrollToTop from "@/components/ScrollToTop";
import { useTranslation } from "react-i18next";

const notBlankRegex = /\S/;
const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;

const validationSchema = (t) => Yup.object().shape({
  name: Yup.string()
    .trim()
    .required(t("brandNameRequired"))
    .min(3, t("brandNameMin"))
    .max(50, t("brandNameMax"))
    .matches(notBlankRegex, t("whitespaceNotAllowed"))
    .matches(noHTMLRegex, t("htmlTagsNotAllowed"))
    .matches(noCodeRegex, t("codeSnippetsNotAllowed"))
    .matches(noConsecutiveSpecialChars, t("consecutiveSpecialCharsNotAllowed"))
    .matches(noLeadingTrailingPunct, t("punctuationAtStartOrEndNotAllowed")),

  description: Yup.string()
    .trim()
    .required(t("brandDescriptionRequired"))
    .min(10, t("brandDescriptionMin"))
    .max(500, t("brandDescriptionMax"))
    .matches(notBlankRegex, t("whitespaceNotAllowed"))
    .matches(noHTMLRegex, t("htmlTagsNotAllowed"))
    .matches(noCodeRegex, t("codeSnippetsNotAllowed"))
    .matches(noConsecutiveSpecialChars, t("consecutiveSpecialCharsNotAllowed"))
    .matches(noLeadingTrailingPunct, t("punctuationAtStartOrEndNotAllowed")),

  image: Yup.mixed()
    .required(t("brandImageRequired"))
    .test("fileType", t("onlyImageFilesAllowed"), (value) => {
      if (!value || !(value instanceof File)) return true;
      return value.type.startsWith("image/");
    })
    .test("fileSize", t("imageSizeTooLarge"), (value) => {
      if (!value || !(value instanceof File)) return true;
      return value.size <= 2 * 1024 * 1024;
    }),

  status: Yup.string()
    .oneOf(["active", "inactive"], t("statusInvalid"))
    .required(t("statusRequired")),
});

export function AddBrand({ isEdit = false, isView = false }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { t } = useTranslation("brand");

  const statusOptions = [{
    label: t('activeLabel'),
    value: 'active'
  },
  {
    label: t('inactiveLabel'),
    value: 'inactive'
  }];

  const selectedBrand = useSelector((s) => s.brand.selected);
  const { loading } = useSelector((state) => state.brand);

  const initialForm = {
    name: "",
    description: "",
    image: null,
    status: "active",
  };

  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm({
    resolver: yupResolver(validationSchema(t)),
    defaultValues: initialForm
  });

  useEffect(() => {
    if (isEdit) {
      if (selectedBrand && String(selectedBrand.id) === String(id)) {
        reset({ ...selectedBrand });
      }
    } else {
      reset(initialForm);
    }
  }, [isEdit, id, selectedBrand, dispatch]);

  const onSubmit = async (data) => {
    try {
      const result = await dispatch(addBrand(data)).unwrap();
      if (result.status) {
        let msg = isEdit ? t("updateSuccessMessage") : t("addSuccessMessage");
        Toaster.success(msg);
        dispatch(resetStatus());
        reset();
        navigate(`/dashboard/brands`);
      } else {
        Toaster.error(result.message || t("somethingWentWrong"));
        dispatch(resetStatus());
      }
    } catch (err) {
      Toaster.error(err?.message || t("addFailedMessage"));
      dispatch(resetStatus());
    }
  };

  const brandImage = watch('image');

  const handleFileChange = (e) => {
    let file = e.target.files[0];
    setValue('image', file, { shouldValidate: true });
  }

  const removeImage = () => {
    setValue('image', null, { shouldValidate: false });
  }

  const getTitle = () => {
    return isView ? t("viewBrandTitle") : isEdit ? t("editBrandTitle") : t("addBrandTitle");
  }

  return (
    <div className="mt-5 mb-8 flex flex-col gap-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {loading && <ShowLoader />}
      <ScrollToTop />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        <div className="sticky top-0 z-30 mt-1 mb-6 p-5 bg-blue-gray-f5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-2 ">
            <div className="flex items-center gap-2 cursor-pointer">
              <Typography variant="h5" color="blue-gray" className="font-extrabold text-gray-800">
                {getTitle()}
              </Typography>
            </div>
            {!isView &&
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate(`/dashboard/brands`)}
                  className="rounded-lg"
                >
                  {t("cancelButton")}
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white btn-material">
                  {loading ? t("savingButton") : isEdit ? t("updateButton") : t("saveButton")}
                </Button>
              </div>
            }
          </div>
          <div className="flex items-center gap-1">
            <Link to={`/dashboard/brands`} className="flex items-center text-blue-600 hover:text-blue-800">
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              {t("backButton")}
            </Link>
          </div>
        </div>
        <fieldset disabled={isView}>
          <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white ">
            <div className="mb-5 pb-2 border-b border-blue-gray-200">
              <Typography variant="h4" color="blue-gray">
                {t("brandDetails")}
              </Typography>
            </div>
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-1">
              <div>
                <Typography
                  variant="small"
                  color="blue-gray"
                  className="mb-3 font-medium"
                >
                  {t("brandNameLabel")} <span className="text-red-500">*</span>
                </Typography>
                <Input
                  type="text"
                  size="lg"
                  className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                  labelProps={{
                    className: "before:content-none after:content-none",
                  }}
                  placeholder={t("enterBrandNamePlaceholder")}
                  {...register('name')}
                  error={Boolean(errors.name)}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                )}
              </div>
              <div>
                <Typography
                  variant="small"
                  color="blue-gray"
                  className="mb-3 font-medium"
                >
                  {t("statusLabel")} <span className="text-red-500">*</span>
                </Typography>
                <Select
                  type="text"
                  size="lg"
                  className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                  labelProps={{
                    className: "before:content-none after:content-none",
                  }}
                  placeholder={t("selectStatusPlaceholder")}
                  onChange={(status) => {
                    setValue('status', status?.value, { shouldValidate: true });
                  }}
                  value={statusOptions?.find(opt => opt.value === watch('status')) || null}
                  options={statusOptions}
                  error={Boolean(errors.status)}
                  isClearable
                  isDisabled={isView}
                />
                {errors.status && (
                  <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>
                )}
              </div>
              <div>
                <Typography
                  variant="small"
                  color="blue-gray"
                  className="mb-3 font-medium"
                >
                  {t("brandDescriptionLabel")} <span className="text-red-500">*</span>
                </Typography>
                <Textarea
                  rows={2}
                  size="lg"
                  className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                  labelProps={{
                    className: "before:content-none after:content-none",
                  }}
                  placeholder={t("enterBrandDescriptionPlaceholder")}
                  {...register('description')}
                  error={Boolean(errors.description)}
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
                )}
              </div>
              <div>
                <Typography variant="small" color="blue-gray" className="mb-3 font-medium">
                  {t("uploadBrandLogoLabel")}  <span className="text-red-500">*</span>
                </Typography>
                <div className="flex flex-wrap items-end gap-6">
                  {!isView && <label htmlFor="brandImage" className="cursor-pointer">
                    <input
                      id="brandImage"
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleFileChange(e)}
                    />
                    <div className="h-36 w-60 bg-white border-2 border-dashed border-blue-400 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-center shadow-sm">
                      <div className="flex flex-col items-center justify-center text-center p-4">
                        <PlusCircleIcon className="h-10 w-10 mb-2 text-blue-500" />
                        <p className="text-base font-medium">{t("uploadImageLabel")}</p>
                      </div>
                    </div>
                  </label>}
                  {brandImage && <FilePreview
                    file={brandImage}
                    onRemove={() => removeImage()}
                    isView={isView}
                    containerClass="relative w-60 h-36 rounded-md overflow-hidden border border-gray-300"
                  />}
                </div>
                {errors.image && (
                  <p className="text-red-500 text-sm mt-2">{errors.image.message}</p>
                )}
              </div>
            </div>
          </div>
        </fieldset>
      </form>
    </div>
  );
}

export default AddBrand;
